import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import InvoiceTable from './components/InvoiceTable';
import InvoiceForm from './components/InvoiceForm';
import InvoiceDetailModal from './components/InvoiceDetailModal';
import PaymentModal from './components/PaymentModal';
import SendInvoiceModal from './components/SendInvoiceModal';
import InvoiceStats from './components/InvoiceStats';
import { supabase } from '../../lib/supabase';

const InvoiceManagementCenter = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('invoices')
        ?.select(`
          *,
          customer:customers(*),
          status:payment_statuses(*),
          invoice_items(*),
          payments(*)
        `)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      setInvoices(data || []);
    } catch (error) {
      console.error('Error fetching invoices:', error?.message);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  const handleCreateInvoice = async (invoiceData) => {
    try {
      const { data: newInvoice, error: invoiceError } = await supabase
        ?.from('invoices')
        ?.insert([{
          customer_id: invoiceData?.customerId,
          ticket_id: invoiceData?.ticketId || null,
          quote_id: invoiceData?.quoteId || null,
          tax_rate: invoiceData?.taxRate,
          discount_amount: invoiceData?.discountAmount,
          due_date: invoiceData?.dueDate,
          notes: invoiceData?.notes,
          invoice_status: 'draft',
        }])
        ?.select()
        ?.single();

      if (invoiceError) throw invoiceError;

      if (invoiceData?.items?.length > 0) {
        const { error: itemsError } = await supabase
          ?.from('invoice_items')
          ?.insert(invoiceData?.items?.map(item => ({
            invoice_id: newInvoice?.id,
            product_id: item?.productId || null,
            description: item?.description,
            quantity: item?.quantity,
            unit_price: item?.unitPrice,
            discount_percent: item?.discountPercent || 0,
          })));
        if (itemsError) throw itemsError;
      }

      showSuccess('Invoice created successfully!');
      setShowInvoiceForm(false);
      fetchInvoices();
    } catch (error) {
      console.error('Error creating invoice:', error?.message);
      alert('Failed to create invoice. Please try again.');
    }
  };

  const handleRecordPayment = async (paymentData) => {
    try {
      const { error } = await supabase
        ?.from('payments')
        ?.insert([{
          invoice_id: paymentData?.invoiceId,
          customer_id: paymentData?.customerId,
          amount: paymentData?.amount,
          payment_method: paymentData?.paymentMethod,
          payment_date: paymentData?.paymentDate,
          transaction_id: paymentData?.transactionId,
          notes: paymentData?.notes,
        }]);

      if (error) throw error;

      showSuccess('Payment recorded successfully!');
      setShowPaymentModal(false);
      setSelectedInvoice(null);
      fetchInvoices();
    } catch (error) {
      console.error('Error recording payment:', error?.message);
      alert('Failed to record payment. Please try again.');
    }
  };

  const handleBulkAction = async (action, ids) => {
    setBulkLoading(true);
    try {
      if (action === 'mark_paid') {
        await supabase?.from('invoices')?.update({
          invoice_status: 'paid',
          paid_date: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString(),
        })?.in('id', ids);
        showSuccess(`${ids?.length} invoice(s) marked as paid`);
      } else if (action === 'mark_overdue') {
        await supabase?.from('invoices')?.update({
          invoice_status: 'overdue',
          updated_at: new Date()?.toISOString(),
        })?.in('id', ids);
        showSuccess(`${ids?.length} invoice(s) marked as overdue`);
      } else if (action === 'send') {
        // For bulk send, just mark as sent (individual send modal for single)
        await supabase?.from('invoices')?.update({
          invoice_status: 'sent',
          sent_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString(),
        })?.in('id', ids);
        showSuccess(`${ids?.length} invoice(s) marked as sent`);
      }
      fetchInvoices();
    } catch (err) {
      console.error('Bulk action failed:', err);
      alert('Bulk action failed. Please try again.');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowDetailModal(true);
  };

  const handleAddPayment = (invoice) => {
    setSelectedInvoice(invoice);
    setShowPaymentModal(true);
  };

  const handleSendInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowSendModal(true);
  };

  const getEffectiveStatus = (inv) => inv?.invoice_status || inv?.status?.name || 'draft';

  const tabCounts = {
    all: invoices?.length,
    draft: invoices?.filter(inv => getEffectiveStatus(inv)?.toLowerCase() === 'draft')?.length,
    sent: invoices?.filter(inv => getEffectiveStatus(inv)?.toLowerCase() === 'sent')?.length,
    pending: invoices?.filter(inv => getEffectiveStatus(inv)?.toLowerCase() === 'pending')?.length,
    partial: invoices?.filter(inv => getEffectiveStatus(inv)?.toLowerCase() === 'partial')?.length,
    paid: invoices?.filter(inv => getEffectiveStatus(inv)?.toLowerCase() === 'paid')?.length,
    overdue: invoices?.filter(inv =>
      getEffectiveStatus(inv)?.toLowerCase() === 'overdue' ||
      (inv?.due_date && new Date(inv?.due_date) < new Date() && !['paid', 'cancelled']?.includes(getEffectiveStatus(inv)?.toLowerCase()))
    )?.length,
  };

  const filteredInvoices = invoices?.filter(invoice => {
    if (activeTab === 'all') return true;
    const status = getEffectiveStatus(invoice)?.toLowerCase();
    if (activeTab === 'overdue') {
      return status === 'overdue' || (
        invoice?.due_date && new Date(invoice?.due_date) < new Date() &&
        !['paid', 'cancelled']?.includes(status)
      );
    }
    return status === activeTab;
  });

  const tabs = ['all', 'draft', 'sent', 'pending', 'partial', 'paid', 'overdue'];

  return (
    <>
      <Helmet>
        <title>Invoice Management - RIGJOCKEY</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 pb-8 px-4 lg:px-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground">Invoice Management</h1>
                <p className="text-muted-foreground mt-1">Create invoices, track payments, and manage billing</p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => window.location.href = '/email-templates'}
                  iconName="Mail"
                  iconPosition="left"
                >
                  Email Templates
                </Button>
                <Button
                  onClick={() => setShowInvoiceForm(true)}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Create Invoice
                </Button>
              </div>
            </div>

            {/* Success Message */}
            {showSuccessMessage && (
              <div className="bg-success/10 border border-success/20 rounded-lg p-4 flex items-center gap-3">
                <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                <span className="text-success font-medium">{successMessage}</span>
              </div>
            )}

            {/* Stats */}
            <InvoiceStats invoices={invoices} loading={loading} />

            {/* Tabs + Table */}
            <div className="bg-card rounded-xl border border-border shadow-elevation-1">
              <div className="border-b border-border px-6">
                <div className="flex gap-1 overflow-x-auto">
                  {tabs?.map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-4 py-3 text-sm font-medium transition-smooth border-b-2 whitespace-nowrap flex items-center gap-1.5 ${
                        activeTab === tab
                          ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {tab?.charAt(0)?.toUpperCase() + tab?.slice(1)}
                      {tabCounts?.[tab] > 0 && (
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                          activeTab === tab ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                        }`}>
                          {tabCounts?.[tab]}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-6">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Icon name="Loader2" size={24} className="animate-spin text-primary" />
                    <span className="ml-3 text-muted-foreground">Loading invoices...</span>
                  </div>
                ) : (
                  <InvoiceTable
                    invoices={filteredInvoices}
                    onViewInvoice={handleViewInvoice}
                    onAddPayment={handleAddPayment}
                    onSendInvoice={handleSendInvoice}
                    onBulkAction={handleBulkAction}
                    onStatusChange={fetchInvoices}
                  />
                )}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Modals */}
      {showInvoiceForm && (
        <InvoiceForm
          isOpen={showInvoiceForm}
          onClose={() => setShowInvoiceForm(false)}
          onSubmit={handleCreateInvoice}
        />
      )}

      {showDetailModal && selectedInvoice && (
        <InvoiceDetailModal
          isOpen={showDetailModal}
          onClose={() => { setShowDetailModal(false); setSelectedInvoice(null); }}
          invoice={selectedInvoice}
          onAddPayment={(inv) => { setShowDetailModal(false); handleAddPayment(inv); }}
          onSendInvoice={(inv) => { setShowDetailModal(false); handleSendInvoice(inv); }}
          onRefresh={fetchInvoices}
        />
      )}

      {showPaymentModal && selectedInvoice && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => { setShowPaymentModal(false); setSelectedInvoice(null); }}
          invoice={selectedInvoice}
          onSubmit={handleRecordPayment}
        />
      )}

      {showSendModal && selectedInvoice && (
        <SendInvoiceModal
          isOpen={showSendModal}
          onClose={() => { setShowSendModal(false); setSelectedInvoice(null); }}
          invoice={selectedInvoice}
          onSent={() => { showSuccess('Invoice sent successfully!'); fetchInvoices(); }}
        />
      )}
    </>
  );
};

export default InvoiceManagementCenter;